import { useCallback } from 'react';
import {
    AssistantInteractionStatus,
    ChatRequestLifecycleEvent,
    ChatRole,
    CHAT_SEND_MESSAGE_TYPE,
    INSTRUCTOR_HUGO_CHAT_TYPE,
} from 'utils/chat/chatConstants';
import { appendPersistedMessage, mergePersistedMessage } from 'utils/chat/chatState';
import { hasMatchingAssistantMessage } from './useInstructorHugoChatHelpers';

const useInstructorHugoMessageLifecycle = ({
    bindLocalChatToPersistedChat,
    chatRef,
    loadThreadSummaries,
    outgoingContext,
    outgoingPageContext,
    reconcilePersistedAssistantMessage,
    refreshActiveThread,
    scope,
    setChat,
    setRuntimeBlocksReconciliation,
}) => {
    const buildOutgoingRequest = useCallback(
        ({ chatId, continuationTarget, requestId, userMessage }) => {
            return {
                type: CHAT_SEND_MESSAGE_TYPE,
                id: requestId,
                request_id: requestId,
                chat_type: INSTRUCTOR_HUGO_CHAT_TYPE,
                chat_id: chatId || null,
                scope,
                ...(outgoingContext ? { context: outgoingContext } : {}),
                page_context: outgoingPageContext,
                created_on: userMessage.created_on,
                message_id: userMessage.id,
                message_text: userMessage.message_text,
                ...(userMessage.interaction_response ? { interaction_response: userMessage.interaction_response } : {}),
                ...(continuationTarget
                    ? {
                          continuation_of_message_id: continuationTarget.sourceMessageId,
                          continuation_requested_interaction_id: continuationTarget.requestedInteraction.id,
                      }
                    : {}),
            };
        },
        [outgoingContext, outgoingPageContext, scope]
    );

    const applyCreatedUserMessage = useCallback(
        ({ userMessage }) => {
            setChat((currentChat) => {
                const userMessageWithContext = {
                    ...userMessage,
                    ...(outgoingContext ? { context: outgoingContext } : {}),
                    page_context: outgoingPageContext,
                    role: ChatRole.USER,
                };
                const nextChat = appendPersistedMessage(currentChat, userMessageWithContext);
                if (!nextChat) return nextChat;

                return {
                    ...nextChat,
                    is_processing: true,
                };
            });
        },
        [outgoingContext, outgoingPageContext, setChat]
    );

    const reconcileAssistantMessageAfterRefresh = useCallback(
        ({ assistantMessage, chatId }) => {
            reconcilePersistedAssistantMessage({
                chatId,
                expectedRequestedInteractionId: assistantMessage.requested_interaction?.id,
                messageId: assistantMessage.id,
            });
            loadThreadSummaries();
        },
        [loadThreadSummaries, reconcilePersistedAssistantMessage]
    );

    const applyCreatedAssistantMessage = useCallback(
        ({ assistantMessage, payload }) => {
            const persistedChatId = payload?.chat_id;
            const activeLocalChatId = chatRef.current?.local_id;

            if (payload?.continuation_of_message_id) {
                refreshActiveThread(persistedChatId || chatRef.current?.id, {
                    force: true,
                    shouldReplaceChat: (currentChat, persistedChat) => currentChat?.id === persistedChat.id,
                })
                    .then((persistedChat) => {
                        if (persistedChat && !persistedChat.is_processing) loadThreadSummaries();
                    })
                    .catch(() => {});
                return;
            }

            setChat((currentChat) => {
                if (!currentChat?.is_processing && hasMatchingAssistantMessage(currentChat, assistantMessage)) {
                    return currentChat;
                }

                const nextChat = mergePersistedMessage(currentChat, assistantMessage);
                if (!nextChat) return nextChat;

                return {
                    ...nextChat,
                    id: persistedChatId || currentChat.id,
                    is_processing: false,
                };
            });
            refreshActiveThread(persistedChatId, {
                force: true,
                shouldReplaceChat: (currentChat, persistedChat) => {
                    return (
                        currentChat?.id === persistedChat.id ||
                        (!currentChat?.id && currentChat?.local_id === activeLocalChatId)
                    );
                },
            })
                .then((persistedChat) => {
                    if (persistedChat) {
                        loadThreadSummaries();
                        return;
                    }

                    reconcileAssistantMessageAfterRefresh({
                        assistantMessage,
                        chatId: persistedChatId,
                    });
                })
                .catch(() => {
                    reconcileAssistantMessageAfterRefresh({
                        assistantMessage,
                        chatId: persistedChatId,
                    });
                });
        },
        [chatRef, loadThreadSummaries, reconcileAssistantMessageAfterRefresh, refreshActiveThread, setChat]
    );

    const applyFailedRequest = useCallback(() => {
        setChat((currentChat) => {
            if (!currentChat) return currentChat;

            return {
                ...currentChat,
                is_processing: false,
            };
        });
    }, [setChat]);

    const onChatRequestStateChange = useCallback(
        (event) => {
            switch (event?.type) {
                case ChatRequestLifecycleEvent.ASSISTANT_MESSAGE_CREATED:
                    applyCreatedAssistantMessage(event);
                    break;
                case ChatRequestLifecycleEvent.CHAT_BOUND:
                    bindLocalChatToPersistedChat(event);
                    break;
                case ChatRequestLifecycleEvent.CONTINUATION_STARTED:
                    setChat((currentChat) => {
                        if (!currentChat) return currentChat;

                        return {
                            ...currentChat,
                            is_processing: true,
                            assistant_interactions: (currentChat.assistant_interactions || []).map((interaction) =>
                                interaction.request?.requested_interaction_id === event.requestedInteractionId
                                    ? { ...interaction, status: AssistantInteractionStatus.PROCESSING }
                                    : interaction
                            ),
                        };
                    });
                    break;
                case ChatRequestLifecycleEvent.REQUEST_FAILED:
                    setRuntimeBlocksReconciliation(false);
                    setChat((currentChat) => {
                        if (!currentChat) return currentChat;

                        return {
                            ...currentChat,
                            is_processing: false,
                            assistant_interactions: (currentChat.assistant_interactions || []).map((interaction) =>
                                interaction.request?.requested_interaction_id === event.requestedInteractionId
                                    ? { ...interaction, status: AssistantInteractionStatus.FAILED }
                                    : interaction
                            ),
                        };
                    });
                    applyFailedRequest(event);
                    break;
                case ChatRequestLifecycleEvent.RUNTIME_STATE_CHANGED:
                    setRuntimeBlocksReconciliation(event.blocksExternalReconciliation);
                    break;
                case ChatRequestLifecycleEvent.USER_MESSAGE_CREATED:
                    applyCreatedUserMessage(event);
                    break;
                default:
                    break;
            }
        },
        [
            applyCreatedAssistantMessage,
            applyCreatedUserMessage,
            applyFailedRequest,
            bindLocalChatToPersistedChat,
            setChat,
            setRuntimeBlocksReconciliation,
        ]
    );

    return {
        buildOutgoingRequest,
        onChatRequestStateChange,
    };
};

export default useInstructorHugoMessageLifecycle;
